import matter from 'gray-matter';
import type { PDFSnippet } from '../services/api';
import { formatMarkdownTable, formatNumber, formatDateTime } from './markdownFormat';
import { cleanMetadata } from './metadataHelpers';

type SnippetLike = PDFSnippet & {
  document_name?: string;
  document_filename?: string;
  frontMatter?: Record<string, any>;
  markdown?: string;
};

interface SerializeSnippetResult {
  markdown: string;
  metadata: Record<string, any>;
  content: string;
}

const formatTextBlock = (label: string, value?: string | null) => {
  if (!value || !value.trim()) return '';
  return `## ${label}\n\n${value.trim()}\n`;
};

const _buildSnippetMetadata = (snippet: SnippetLike): Record<string, any> => {
  return {
    title: snippet.title || `Fragmento página ${snippet.page_number}`,
    document_id: snippet.document_id,
    document_name: snippet.document_name,
    document_filename: snippet.document_filename,
    page_number: snippet.page_number,
    snippet_type: snippet.snippet_type,
    coordinates: snippet.coordinates,
    tags: snippet.tags ?? [],
    notes: snippet.notes,
    created_at: formatDateTime(snippet.created_at),
    updated_at: formatDateTime(snippet.updated_at),
  };
};


const _buildSnippetContentBlocks = (snippet: SnippetLike): string[] => {
  const blocks: string[] = [];

  const textBlock = formatTextBlock('Texto seleccionado', snippet.text_content);
  if (textBlock) blocks.push(textBlock);

  const contextBlock = formatTextBlock('Contexto', snippet.context_text);
  if (contextBlock) blocks.push(contextBlock);

  if (snippet.snippet_type === 'image' && snippet.image_data) {
    blocks.push('## Imagen\n\n' + `![Fragmento](${snippet.image_data})`);
  }

  const notesBlock = formatTextBlock('Notas', snippet.notes);
  if (notesBlock) blocks.push(notesBlock);

  if (Array.isArray(snippet.tags) && snippet.tags.length > 0) {
    blocks.push('## Etiquetas\n\n' + snippet.tags.map(tag => `- ${tag}`).join('\n'));
  }

  if (snippet.coordinates) {
    const coord = snippet.coordinates as Record<string, any>;
    const table = formatMarkdownTable([
      Object.fromEntries(
        Object.entries(coord).map(([key, value]) => [key, typeof value === 'number' ? formatNumber(value) : value])
      )
    ]);
    blocks.push('## Coordenadas\n\n' + table);
  }

  return blocks;
};

export const serializeSnippetToMarkdown = (snippet: SnippetLike): SerializeSnippetResult => {
  const metadata = _buildSnippetMetadata(snippet);

  if (snippet.context_text && !metadata.context_preview) {
    metadata.context_preview = snippet.context_text.slice(0, 240);
  }

  cleanMetadata(metadata);

  const blocks = _buildSnippetContentBlocks(snippet);
  const content = blocks.join('\n\n').trim() || '*Sin contenido disponible*';

  return {
    markdown: matter.stringify(content, metadata),
    metadata,
    content,
  };
};

export const parseSnippetMarkdown = (markdown?: string) => {
  try {
    return matter(markdown ?? '');
  } catch (error) {
    console.error('Error parsing snippet markdown:', error);
    return matter('');
  }
};
